<template>
  <div
    class="your-tasks-table-container"
    :class="{ 'your-tasks-table-container--embedded': embedded }"
  >
    <!-- Top Search and Filter Section -->
    <div class="table-header-actions mb-4">
      <div class="search-filter-wrapper w-100">
        <SearchByModal
          v-if="showSearchBar"
          :label="visibleFilterCount > 0 ? 'More Filters' : 'Filter'"
          :length="visibleFilterCount"
          :chipText="filterChipLabels"
          ghostText="Search Summary"
          :valueSearch="searchQuery"
          @onSearch="handleSearch"
          @removeFilter="handleRemoveFilter"
          @onCheckedItemsChanged="handleFilteredModal"
          :filterType="'Tasks'"
          :filterGroups="showMenuColumn ? allTasksFilterGroups : []"
          :groupsItems="showMenuColumn ? allTasksGroupsItems : []"
          :statusOptions="statusOptionsForModal"
          :defaultSelected="filterModalDefaultSelected"
          :modalWidth="showMenuColumn ? 480 : 600"
          class="tasks-search-bar"
        />
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-responsive">
      <SharedTableSkeleton
        v-if="loading"
        :row-count="skeletonRowCount"
        :column-count="skeletonColumnCount"
        :column-template="skeletonColumnTemplate"
      />
      <table v-else-if="paginatedTasks.length > 0" class="tasks-table">
        <colgroup>
          <col class="col-icon" />
          <col class="col-summary" />
          <col class="col-assigned-to" />
          <col class="col-assigned" />
          <col class="col-status" />
          <col class="col-primary-action" />
          <col class="col-chatter" />
          <col v-if="showMenuColumn" class="col-menu" />
        </colgroup>
        <thead>
          <tr>
            <th class="column-icon"></th>
            <th class="column-summary">Summary</th>
            <th class="column-assigned-to">Assigned To</th>
            <th class="column-assigned">Assigned By/On</th>
            <th class="column-status">Status</th>
            <th class="column-primary-action" scope="col">
              <span class="sr-only">Action</span>
            </th>
            <th class="column-chatter" scope="col">
              <span class="sr-only">Chatter</span>
            </th>
            <th v-if="showMenuColumn" class="column-menu" scope="col">
              <span class="sr-only">Menu</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="task in rowsWithActions" :key="task.id">
            <td class="column-icon">
              <div class="task-icon-wrapper">
                <img :src="task.iconSrc" alt="" class="task-icon" width="24" height="24" />
              </div>
            </td>
            <td class="column-summary">
              <div class="summary-content">
                <div class="summary-title">{{ summaryFirstLine(task) }}</div>
                <div v-if="task.responsibility" class="summary-subtitle">
                  {{ task.responsibility }}
                </div>
              </div>
            </td>
            <td class="column-assigned-to">
              <div class="stamp-cell">
                <Stamp
                  :name="task.assignedToCell.name"
                  :avatar="task.assignedToCell.avatar"
                  :date="task.assignedToCell.date"
                  :show_time="true"
                />
              </div>
            </td>
            <td class="column-assigned">
              <div class="stamp-cell">
                <template v-if="task.assignedByCell.type === 'stamp'">
                  <Stamp
                    :name="task.assignedByCell.name"
                    :avatar="task.assignedByCell.avatar"
                    :date="task.assignedByCell.date"
                    :show_time="true"
                  />
                </template>
                <span
                  v-else-if="task.assignedByCell.type === 'date' && task.assignedByCell.dateLabel"
                  class="assigned-on-only"
                >
                  {{ task.assignedByCell.dateLabel }}
                </span>
              </div>
            </td>
            <td class="column-status">
              <TaskStatusBadge :taskStatus="task.status" :date="task.statusDate" />
            </td>
            <td class="column-primary-action">
              <StandardButton
                v-if="task.action"
                :type="task.action.buttonType"
                variant="primary"
                size="xs"
                :label="task.action.label"
                @click="onPrimaryActionClick(task)"
              />
            </td>
            <td class="column-chatter">
              <ChatterIcon :count="task.chatterCount || 0" @onClick="emit('chatter', task)" />
            </td>
            <td v-if="showMenuColumn" class="column-menu">
              <MenuIcon
                :vertical="true"
                :showTooltip="false"
                :dropdown-items="task.menuItems || []"
                @onClick="onMenuItemClick($event, task)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State: No search/filter applied, but no tasks exist -->
      <NoRecordFound
        v-else-if="tasks.length === 0 && !isFiltered"
        title=""
        message=""
        btnTitle=""
        :shouldShowAddAction="false"
        :showActiveCounter="false"
        :noBorder="true"
        showSpecificMessage="You currently have no Tasks"
        description="There are no current tasks assigned to you at this time"
      />

      <!-- Empty State: Search/filter applied, but no matching tasks -->
      <NoRecordFound
        v-else
        title=""
        message=""
        btnTitle=""
        :shouldShowAddAction="false"
        :showActiveCounter="false"
        :noBorder="true"
        :showClearAction="true"
        :clearLabel="clearActionLabel"
        showSpecificMessage="No tasks found"
        :description="noResultsDescription"
        @onClear="handleClearSearch"
      />
    </div>

    <!-- Pagination Section -->
    <div v-if="internalPagination.total > internalPagination.perPage" class="table-pagination">
      <ExPagination :pagination="internalPagination" @onRequest="handlePaginationRequest" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import SearchByModal from "@components/SearchBar/SearchByModal.vue";
import TaskStatusBadge from "@components/TaskStatusBadge/TaskStatusBadge.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import Stamp from "@components/Stamp/Stamp.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import {
  resolveAssignedByCell,
  resolveAssignedToCell,
  resolveTaskIconKind,
  summaryFirstLine,
  taskMenuItems,
} from "@components/TasksTable/tasksTableCellUtils";
import {
  ALL_TASKS_ASSIGNEE_FILTER_VALUE,
  MY_TASKS_FILTER_LABEL,
  MY_TASKS_FILTER_VALUE,
  TASKS_ASSIGNEE_FILTER_GROUP,
  isMyTasksAssigneeFilter,
} from "@components/constants/tasksModule";

const props = defineProps({
  tasks: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  menuItems: {
    type: Array,
    default: () => [],
  },
  serverPaginated: {
    type: Boolean,
    default: false,
  },
  /** When true, omit outer card chrome (used inside TasksPage panel). */
  embedded: {
    type: Boolean,
    default: false,
  },
  /** Row overflow menu column (All Tasks only; hidden on Your Stuff > Your Tasks). */
  showMenuColumn: {
    type: Boolean,
    default: true,
  },
  /** MDP-560 — parent-fed fetch state; when true the shared skeleton replaces the grid and empty states. */
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "search",
  "filter",
  "removeFilter",
  "paginationChange",
  "upload",
  "done",
  "register",
  "chatter",
  "menuAction",
  "onFilterChange",
]);

const searchQuery = ref("");
const clearSelection = ref("");
const localAppliedFilters = ref([...props.appliedFilters]);

const YOUR_TASKS_STATUS_OPTIONS = ["To Do", "In Progress", "Completed", "Canceled"];

const STATUS_LABEL_BY_VALUE = {
  to_do: "To Do",
  in_progress: "In Progress",
  completed: "Completed",
  canceled: "Canceled",
  cancelled: "Canceled",
};

const STATUS_VALUE_BY_LABEL = {
  "To Do": "to_do",
  "In Progress": "in_progress",
  Completed: "completed",
  Canceled: "canceled",
  Cancelled: "canceled",
};

const allTasksFilterGroups = ref([]);

const allTasksGroupsItems = ref([
  {
    groups: [
      {
        type: "check",
        label: "Status",
        items: [
          { label: "To Do", value: "to_do" },
          { label: "Completed", value: "completed" },
          { label: "Canceled", value: "canceled" },
        ],
      },
      {
        type: "radio",
        label: TASKS_ASSIGNEE_FILTER_GROUP,
        items: [
          { label: "All", value: ALL_TASKS_ASSIGNEE_FILTER_VALUE },
          { label: MY_TASKS_FILTER_LABEL, value: MY_TASKS_FILTER_VALUE },
        ],
      },
    ],
  },
]);

const statusOptionsForModal = computed(() =>
  props.showMenuColumn ? [] : YOUR_TASKS_STATUS_OPTIONS
);

function isHiddenAssigneeFilterToken(filter) {
  const key = String(filter || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  return key === ALL_TASKS_ASSIGNEE_FILTER_VALUE || key === "all";
}

const visibleFilterCount = computed(() =>
  localAppliedFilters.value.filter((filter) => !isHiddenAssigneeFilterToken(filter)).length
);

const filterChipLabels = computed(() =>
  localAppliedFilters.value
    .filter((filter) => !isHiddenAssigneeFilterToken(filter))
    .map((filter) => {
      if (isMyTasksAssigneeFilter(filter)) return MY_TASKS_FILTER_LABEL;
      return STATUS_LABEL_BY_VALUE[filter] || filter;
    })
);

const filterModalDefaultSelected = computed(() => {
  const selected = [...localAppliedFilters.value];
  if (props.showMenuColumn && !selected.some(isMyTasksAssigneeFilter)) {
    selected.push(ALL_TASKS_ASSIGNEE_FILTER_VALUE);
  }
  return selected;
});

function mapFilterItemToStoredValue(item) {
  if (typeof item === "string") return item;
  if (item?.value == null) return null;
  if (item.labelType === TASKS_ASSIGNEE_FILTER_GROUP) {
    if (item.value === MY_TASKS_FILTER_LABEL) return MY_TASKS_FILTER_VALUE;
    return null;
  }
  return item.value;
}

const _uploadCloud = require("@components/assets/upload-cloud-02.svg");
const _qrCode = require("@components/assets/qr-code-01.svg");
const _checkSquare = require("@components/assets/check-square.svg");

const iconSrc = (asset) =>
  asset && typeof asset === "object" && "default" in asset ? asset.default : asset;

function taskStatusKey(task) {
  return (task.status || "").toLowerCase().replace(/\s+/g, "_");
}

function taskStatusDate(task) {
  const status = taskStatusKey(task);
  if (status === "completed" || status === "canceled") return task.doneOn ?? null;
  return task.deadline ?? null;
}

function taskIconSrc(task) {
  const kind = resolveTaskIconKind(task);
  if (kind === "check") return iconSrc(_checkSquare);
  if (kind === "qr") return iconSrc(_qrCode);
  return iconSrc(_uploadCloud);
}

function taskAction(task) {
  if (task.showUpload) {
    return { label: "Upload", buttonType: "magenta", event: "upload" };
  }
  const status = taskStatusKey(task);
  const type = task.type || "";
  if (status === "completed" || status === "canceled") return null;
  if (status === "in_progress") {
    return { label: "Done", buttonType: "green", event: "done" };
  }
  if (type.includes("qr") || type.includes("register")) {
    return { label: "Register", buttonType: "magenta", event: "register" };
  }
  if (status === "to_do" && task.isCurrentUserAssignee) {
    return { label: "Upload", buttonType: "magenta", event: "upload" };
  }
  return null;
}

function rowMenuItems(task) {
  return taskMenuItems(task, props.menuItems);
}

function onPrimaryActionClick(task) {
  const action = task.action || taskAction(task);
  if (action?.event) {
    emit(action.event, task);
  }
}

function onMenuItemClick(item, task) {
  if (!item) return;
  if (item.action) {
    emit("menuAction", { item, task });
  } else if (item.event) {
    emit(item.event, task);
  }
}

const internalPagination = ref({
  page: props.pagination.page || 1,
  total: props.pagination.total || 0,
  perPage: props.pagination.perPage || 10,
});

const noResultsDescription = computed(() => {
  if (searchQuery.value && localAppliedFilters.value.length > 0) {
    return "Your search and filter did not match any tasks. Please try again.";
  }
  if (searchQuery.value) {
    return "Your search did not match any tasks. Please try again.";
  }
  return "Your filter did not match any tasks. Please try again.";
});

const clearActionLabel = computed(() => {
  if (searchQuery.value && localAppliedFilters.value.length > 0) {
    return "Clear all";
  }
  if (searchQuery.value) {
    return "Clear search";
  }
  return "Clear filter";
});

const skeletonRowCount = computed(() => props.pagination?.perPage || 10);

const skeletonColumnCount = computed(() => (props.showMenuColumn ? 8 : 7));

const skeletonColumnTemplate = computed(() =>
  props.showMenuColumn
    ? "24px 3fr 1.5fr 1.5fr 100px 80px 32px 32px"
    : "24px 3fr 1.5fr 1.5fr 100px 80px 32px"
);

const isFiltered = computed(() => {
  return searchQuery.value.length > 0 || localAppliedFilters.value.length > 0;
});

const showSearchBar = computed(() => props.tasks.length > 0 || isFiltered.value);

const filteredTasks = computed(() => {
  let result = [...props.tasks];

  if (searchQuery.value && !props.serverPaginated) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (task) =>
        (task.summary?.toLowerCase() || "").includes(query) ||
        (task.criteria?.toLowerCase() || "").includes(query) ||
        (task.responsibility?.toLowerCase() || "").includes(query)
    );
  }

  if (!props.serverPaginated && localAppliedFilters.value.length > 0) {
    result = result.filter((task) => {
      const normalizedStatus = task.status?.toLowerCase().replace(/\s+/g, "_") || "";
      return localAppliedFilters.value.some((filter) => {
        const normalizedFilter = filter.toLowerCase().replace(/\s+/g, "_");
        return normalizedStatus === normalizedFilter;
      });
    });
  }

  return result;
});

const paginatedTasks = computed(() => {
  if (props.serverPaginated) {
    return filteredTasks.value;
  }
  const start = (internalPagination.value.page - 1) * internalPagination.value.perPage;
  const end = start + internalPagination.value.perPage;
  return filteredTasks.value.slice(start, end);
});

const rowsWithActions = computed(() =>
  paginatedTasks.value.map((task) => ({
    ...task,
    action: taskAction(task),
    iconSrc: taskIconSrc(task),
    statusDate: taskStatusDate(task),
    menuItems: rowMenuItems(task),
    assignedToCell: resolveAssignedToCell(task),
    assignedByCell: resolveAssignedByCell(task),
  }))
);

watch(
  () => filteredTasks.value.length,
  (newLength) => {
    if (!props.serverPaginated) {
      internalPagination.value.total = newLength;
    }
  }
);

watch(
  () => props.appliedFilters,
  (newVal) => {
    localAppliedFilters.value = [...newVal];
  },
  { deep: true }
);

watch(
  () => props.pagination,
  (newVal) => {
    if (!newVal) return;
    internalPagination.value = {
      page: newVal.page || 1,
      total: props.serverPaginated ? newVal.total || 0 : filteredTasks.value.length,
      perPage: newVal.perPage || 10,
    };
  },
  { deep: true, immediate: true }
);

const handleSearch = (val) => {
  searchQuery.value = val;
  internalPagination.value.page = 1;
  emit("search", val);
};

const handleFilteredModal = (filters) => {
  const newFilters = (Array.isArray(filters) ? filters : [])
    .map(mapFilterItemToStoredValue)
    .filter(Boolean);

  localAppliedFilters.value = newFilters;
  internalPagination.value.page = 1;
  emit("onFilterChange", newFilters);
};

const handleRemoveFilter = (removedFilter) => {
  const valueToRemove =
    STATUS_VALUE_BY_LABEL[removedFilter] ??
    (removedFilter === MY_TASKS_FILTER_LABEL ? MY_TASKS_FILTER_VALUE : removedFilter);

  localAppliedFilters.value = localAppliedFilters.value.filter(
    (filter) =>
      filter !== valueToRemove &&
      STATUS_LABEL_BY_VALUE[filter] !== removedFilter &&
      !(removedFilter === MY_TASKS_FILTER_LABEL && isMyTasksAssigneeFilter(filter))
  );
  clearSelection.value =
    removedFilter === MY_TASKS_FILTER_LABEL ? MY_TASKS_FILTER_LABEL : valueToRemove;
  emit("removeFilter", removedFilter);
};

const handleClearSearch = () => {
  searchQuery.value = "";
  localAppliedFilters.value = [];
  internalPagination.value.page = 1;
  emit("search", "");
  emit("onFilterChange", []);
};

const handlePaginationRequest = (data) => {
  internalPagination.value.page = data.page;
  emit("paginationChange", data);
};

defineExpose({
  handleSearch,
  handleFilteredModal,
  handleRemoveFilter,
  handleClearSearch,
  handlePaginationRequest,
  searchQuery,
  localAppliedFilters,
  internalPagination,
});
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.your-tasks-table-container {
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  min-width: 0;
  max-width: 100%;
  /* Keep radius; row actions need visible overflow for menu hover background. */
  overflow: visible;
}

.your-tasks-table-container--embedded {
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.table-responsive {
  min-width: 0;
  max-width: 100%;
  /* Fit the panel; summary/status truncate instead of horizontal scroll. */
  overflow-x: hidden;
}

.table-header-actions {
  padding: 12px 25px 0px 25px;
}

.search-filter-wrapper {
  padding: 8px 0;
}

:deep(.tasks-search-bar .filter-button) {
  padding: 0 !important;
}

:deep(.tasks-search-bar .search-button) {
  padding: 0 !important;
  justify-content: flex-end !important;
}

:deep(.tasks-search-bar .search-bar),
:deep(.tasks-search-bar .more-search-bar) {
  border: 1px solid #d0d5dd !important;
  border-radius: 8px !important;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05) !important;
  background-color: white !important;
  color: #344054 !important;
  font-weight: 600 !important;
}

:deep(.tasks-search-bar .filter-input-search) {
  width: 320px !important;
  flex: none !important;
}

.tasks-table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
  font-family: Inter, sans-serif;
  border-top: 1px solid #eaecf0;
}

.tasks-table col.col-icon {
  width: 4%;
}

.tasks-table col.col-summary {
  width: 46%;
}

.tasks-table col.col-assigned-to {
  width: 14%;
}

.tasks-table col.col-assigned {
  width: 17%;
}

.tasks-table:has(col.col-assigned-to) col.col-summary {
  width: 32%;
}

.tasks-table:has(col.col-assigned-to) col.col-assigned {
  width: 14%;
}

.tasks-table col.col-status {
  width: 10%;
}

.tasks-table col.col-primary-action {
  width: 7%;
}

.tasks-table col.col-chatter {
  width: 4%;
}

.tasks-table col.col-menu {
  width: 8%;
}

.tasks-table th {
  text-align: left;
  padding: 12px 10px;
  background-color: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  border-top: 1px solid transparent;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-transform: none;
}

.tasks-table td {
  padding: 16px 10px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
  box-sizing: border-box;
}

.tasks-table .column-icon {
  padding-left: 24px;
  padding-right: 0;
}

.column-icon {
  width: 4%;
}

.task-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.column-summary {
  max-width: 0;
  overflow: hidden;
  padding-left: 12px;
  padding-right: 12px;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  max-width: 100%;
}

.summary-title,
.summary-subtitle {
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
}

.task-icon {
  width: 24px;
  height: 24px;
  opacity: 0.6;
}

.column-assigned,
.column-primary-action {
  overflow: hidden;
  min-width: 0;
}

.column-status,
.column-chatter,
.column-menu {
  min-width: 0;
  overflow: visible;
}

.column-assigned,
.column-assigned-to {
  text-align: center;
}

.tasks-table th.column-assigned,
.tasks-table th.column-assigned-to {
  text-align: center;
}

.assigned-on-only {
  font-size: 14px;
  color: #344054;
}

.stamp-cell {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-width: 0;
}

.stamp-cell :deep(.stamp-wrapper) {
  align-items: center !important;
  margin: 0 !important;
}

.stamp-cell :deep(.stamp-date-text) {
  margin-left: 0 !important;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.stamp-cell :deep(.with-stamp-eval) {
  max-width: 100%;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.stamp-cell :deep(.v-list-item__title) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.column-status {
  vertical-align: middle;
  padding: 12px 8px;
}

.column-primary-action {
  vertical-align: middle;
  padding: 12px 12px 12px 8px;
}

.column-primary-action :deep(.standard-button) {
  min-width: 0 !important;
  width: auto;
  max-width: 100%;
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
}

.column-chatter {
  vertical-align: middle;
  padding: 12px 8px;
  text-align: center;
}

.column-menu {
  vertical-align: middle;
  padding: 12px 20px 12px 8px;
  text-align: center;
}

.tasks-table th.column-menu {
  padding-right: 20px;
}

.column-chatter :deep(.chatter-icon-com) {
  margin-left: auto;
  margin-right: auto;
}

.column-menu :deep(.ex-menu-icon) {
  margin: 0 auto;
}

.table-pagination {
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.pagination-container) {
  width: 100%;
  justify-content: space-between !important;
  padding: 0 !important;
}

:deep(.data-table-pagination) {
  flex: 1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .column-assigned,
  .column-assigned-to,
  .column-status,
  .column-primary-action {
    display: none;
  }
}
</style>
